import { useEffect, useMemo, useState } from 'react';

import { useOutletContext, useRouteLoaderData } from '@remix-run/react';

import { usePageContext } from '~/context/usePageContext';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { RootLoaderData } from '~/root';

import useIsLoggedIn from './useIsLoggedIn';
import useScrollPosition from './useScrollPosition';

const PERCENTAGE_SCROLLED_BEFORE_SHOWING_MODAL = 40;

const makeCookieName = (pageId: string) => `email_capture_modal_closed_${pageId}`;

export const useEmailCaptureModal = ({
  showAfterPercentageScrolled = PERCENTAGE_SCROLLED_BEFORE_SHOWING_MODAL,
  cookieExpiryDays,
  pageId,
  enabled,
  isOnPostPage,
}: {
  pageId: string;
  enabled: boolean;
  showAfterPercentageScrolled?: number;
  cookieExpiryDays?: number;
  isOnPostPage?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { setOpenPageModalType, closeModalActionDispatched, setCloseModalActionDispatched } = usePageContext();
  const { isStagingPreview } = useRouteLoaderData('root') as RootLoaderData;
  const { setCookie } = useOutletContext<OutletContext>();

  const isLoggedIn = useIsLoggedIn();
  const { cookies } = useOutletContext<OutletContext>();

  const isPopupDisabled = !isStagingPreview && (Boolean(cookies[makeCookieName(pageId)]) || isLoggedIn);

  const { percentageScrolled } = useScrollPosition() || {
    percentageScrolled: 0,
    windowScrollY: 0,
  };
  const currentProgress = Number((percentageScrolled * 100).toFixed(1));

  const showPopup = useMemo(() => {
    if (isPopupDisabled) {
      return false;
    }

    return currentProgress >= showAfterPercentageScrolled;
  }, [currentProgress, isPopupDisabled, showAfterPercentageScrolled]);

  // Open popup
  useEffect(() => {
    if (!enabled || isOpen) {
      return;
    }

    if (showPopup) {
      if (cookieExpiryDays) {
        setCookie({ name: makeCookieName(pageId), value: 'true', days: cookieExpiryDays });
      }

      if (isOnPostPage) {
        setOpenPageModalType('email_capture');
      }

      setIsOpen(true);
    }
  }, [enabled, isOpen, showPopup, cookieExpiryDays, pageId, isOnPostPage]);

  // Close popup if close_modal action is dispatched from a Button
  useEffect(() => {
    if (isOpen && closeModalActionDispatched) {
      setIsOpen(false);
      setCloseModalActionDispatched(false);
      setOpenPageModalType(null);
    }
  }, [isOpen, closeModalActionDispatched]);

  return {
    isOpen: isOpen,
    onClose: () => {
      setIsOpen(false);
      setCookie({ name: makeCookieName(pageId), value: 'true', days: cookieExpiryDays || 4 });
      if (isOnPostPage) {
        setOpenPageModalType(null);
      }
    },
  };
};
